
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, Users, Plus } from "lucide-react";
import { Job } from '@/components/jobs/types';

interface InterviewManagementTabProps {
  job: Job;
}

// Simple mock data for demonstration
const mockInterviews = [
  {
    id: '1',
    candidateName: 'Sarah Johnson',
    position: 'Senior Developer',
    date: '2024-01-20',
    time: '10:00 AM',
    type: 'Technical Interview',
    status: 'scheduled',
    interviewer: 'John Smith'
  },
  {
    id: '2',
    candidateName: 'Michael Chen',
    position: 'Senior Developer',
    date: '2024-01-22',
    time: '2:00 PM',
    type: 'Final Interview',
    status: 'completed',
    interviewer: 'Sarah Wilson'
  }
];

const getStatusColor = (status: string) => {
  switch (status) {
    case 'scheduled': return 'bg-blue-100 text-blue-800';
    case 'completed': return 'bg-green-100 text-green-800';
    case 'cancelled': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

export const InterviewManagementTab: React.FC<InterviewManagementTabProps> = ({ job }) => {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Interview Schedule for {job.title}
            </CardTitle>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Schedule Interview
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {mockInterviews.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No interviews scheduled</h3>
              <p className="text-gray-500 mb-4">Get started by scheduling your first interview for this position.</p>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Schedule First Interview
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {mockInterviews.map((interview) => (
                <div key={interview.id} className="border rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold text-lg">{interview.candidateName}</h4>
                        <Badge className={getStatusColor(interview.status)}>
                          {interview.status}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          <span>{interview.date}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          <span>{interview.time}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          <span>{interview.interviewer}</span>
                        </div>
                      </div>
                      <div className="mt-2">
                        <span className="text-sm font-medium">Type: </span>
                        <span className="text-sm text-gray-600">{interview.type}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm">
                        Edit
                      </Button>
                      <Button variant="outline" size="sm">
                        View Details
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
